import { useState } from "react";
import { Check, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, http } from "@/lib/api";

const toFile = async (dataUrl, name) => new File([await (await fetch(dataUrl)).blob()], `${name || "logo"}.png`, { type: "image/png" });
const checker = "bg-[conic-gradient(#e2e8f0_25%,#fff_0_50%,#e2e8f0_0_75%,#fff_0)] bg-[length:16px_16px]";

export function WebsiteFill({ name, website, onWebsite, onLogo, onProfile }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [picked, setPicked] = useState(null);

  const fetchSite = async () => {
    if (!name.trim() || !website.trim()) { toast.error("Enter the brand name and its official website first"); return; }
    setBusy(true); setRes(null); setPicked(null);
    try {
      const { data } = await http.post("/admin/brands/enrich", { name, website }, { timeout: 90000 });
      setRes(data);
      if (data?.website) onWebsite(data.website);
      if (!data?.logos?.length && !data?.profile) toast.info("Nothing usable found on that website. You can still fill it in yourself.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pick = async (l, i) => { setPicked(i); onLogo(await toFile(l.data_url, name.toLowerCase().replace(/\W+/g, "-")), l.data_url); };

  return (
    <div className="mt-2" data-testid="website-fill">
      <button type="button" onClick={fetchSite} disabled={busy} data-testid="website-fill-btn"
        className="inline-flex items-center gap-2 rounded-full border border-ph-line px-4 py-2 text-xs font-semibold text-ph-ink transition-colors hover:bg-ph-raised disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />} {busy ? "Reading the website…" : "Fill logo & profile from website"}
      </button>
      {res && (
        <div className="mt-3 space-y-4 rounded-2xl border border-ph-line bg-ph-raised/50 p-4" data-testid="website-fill-results">
          <div>
            <p className="text-xs font-semibold text-ph-ink">Logo found {res.logos?.length ? "— tap one to use it" : ""}</p>
            {res.logos?.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {res.logos.map((l, i) => (
                  <button key={l.source} type="button" onClick={() => pick(l, i)} title={l.source} data-testid={`website-fill-logo-${i}`}
                    className={`relative flex h-20 w-32 items-center justify-center rounded-xl border-2 p-2 ${checker} ${picked === i ? "border-ph-accent" : "border-transparent"}`}>
                    <img src={l.data_url} alt={`Logo option ${i + 1}`} className="max-h-full max-w-full object-contain" />
                    {picked === i && <Check className="absolute right-1 top-1 h-4 w-4 rounded-full bg-ph-accent p-0.5 text-white" />}
                  </button>
                ))}
              </div>
            ) : <p className="mt-1 text-xs text-ph-muted">No logo could be read. Upload one below.</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-ph-ink">Profile (written by AI in our own words)</p>
            {res.profile ? (
              <>
                <p className="mt-1 text-sm text-ph-ink/85" data-testid="website-fill-profile">{res.profile}</p>
                <p className="mt-1 truncate text-[11px] text-ph-muted">Source: {res.profile_source}</p>
                <button type="button" onClick={() => { onProfile(res.profile, res.profile_source); toast.success("Profile added to the description — edit it if needed"); }} data-testid="website-fill-use-profile"
                  className="mt-2 rounded-full bg-ph-ink px-4 py-1.5 text-xs font-semibold text-ph-bg">Use this profile</button>
              </>
            ) : <p className="mt-1 text-xs text-ph-muted">Not enough information on the website to write a profile.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
